import { ChangeEvent } from "react";
// MUI
import { Card, CardActions, CardContent, Checkbox, FormControlLabel, Grid, Stack, Typography } from "@mui/material";

// Hooks
import { useAuthorization } from "@Common/hooks/Authorization/Authorization";

// Constants
import ACTIONS from "@Constants/actionsList";

// Components
import AssignmentStatusCardSection from "@PayPlans/components/Assignments/Contents/Group/AssignmentStatusCardSection.tsx";

// Types
import { IUser } from "@Common/interfaces/user";

type IAssignmentCardProps = Readonly<{
	data: (Omit<IUser, "_id"> & { _id: string })[];
	position: string;
	itemKey: string;
	selectedList: string[];
	isPending: boolean;
	// Callbacks
	selectItemHandler: (id: string, e: ChangeEvent<HTMLInputElement>) => void;
	onOpenPayPlanModel: (id: string) => void;
}>;

export default function AssignmentCardGroup(props: IAssignmentCardProps) {
	const { useAuth } = useAuthorization();

	const VIEW_SALES_ASSOCIATE_PAY_PLANS = useAuth(ACTIONS.VIEW_SALES_ASSOCIATE_PAY_PLANS);

	const {
		data,
		position,
		selectedList,
		isPending,
		// Callbacks
		selectItemHandler,
		onOpenPayPlanModel,
	} = props;
	const groupIds = data.filter((item) => item.position === position).map((item) => item._id);
	// All are selected if groupIds is not empty and every id is in selectedList
	const isGroupSelectedAll = groupIds.length > 0 && groupIds.every((id) => selectedList.includes(id));

	// Indeterminate if at least one item is selected but not all
	const isGroupIndeterminate = groupIds.some((id) => selectedList.includes(id)) && !isGroupSelectedAll;
	const selectAllHandler = (e: ChangeEvent<HTMLInputElement>) => {
		const checked = e.target.checked;
		data.filter((item) => item.position === position).forEach((item) => {
			// If checking, only add items that aren't already selected
			if (checked && !selectedList.includes(item._id)) {
				selectItemHandler(item._id, e);
			}
			// If unchecking, remove items that are selected
			if (!checked && selectedList.includes(item._id)) {
				selectItemHandler(item._id, e);
			}
		});
	};

	return (
		<Stack>
			<Typography
				variant="h6"
				className="!font-montserrat !text-lg !text-primary !font-medium "
				sx={{
					paddingTop: 2,
				}}
			>
				{position}
			</Typography>
			<Grid item key={position} className="!mt-[5px] !pl-[5px] !mb-[12px]">
				<FormControlLabel
					control={
						<Checkbox
							disabled={data?.length === 0}
							checked={isGroupSelectedAll}
							indeterminate={isGroupIndeterminate}
							onChange={selectAllHandler}
						/>
					}
					label={<Typography className="group-pay-plan-assign-container-select-all">Select All</Typography>}
				/>
			</Grid>
			<Grid container columnSpacing={2.5} rowSpacing={4}>
				{data?.map((employee) => {
					return (
						<Grid item xs={3} key={employee?._id}>
							<Card classes={{ root: "px-2 !rounded-[10px] !shadow-card-gray-light" }}>
								<Stack>
									<CardActions
										classes={{
											spacing: "!p-0 !pl-1",
											root: "!p-0 !mb-[-7px]",
										}}
									>
										<FormControlLabel
											control={
												<Checkbox
													checked={selectedList.includes(employee?._id)}
													onChange={(e) => selectItemHandler(employee?._id, e)}
												/>
											}
											label={
												<Typography className="!text-xs !font-medium !font-montserrat">
													{employee?.fullName}
												</Typography>
											}
											labelPlacement="end"
											className="payroll-submission-position"
										/>
									</CardActions>

									<CardContent classes={{ root: "!m-0 !p-0 !pl-7" }}>
										<Grid container direction="column">
											<AssignmentStatusCardSection
												assignmentPhase={"Upcoming"}
												selectedList={selectedList}
												isLoading={isPending}
												isDisabled={!VIEW_SALES_ASSOCIATE_PAY_PLANS}
												employee={employee}
												payPlanInfo={{
													payPlanId: employee?.upcomingPayPlanAssignment?.planId?._id,
													payPlanName: employee?.upcomingPayPlanAssignment?.planId?.name,
													acknowledgementDate: employee?.upcomingPayPlanAssignment?.acknowledgementDate,
													assignmentDate: employee?.upcomingPayPlanAssignment?.assignmentDate,
												}}
												onClickPayPlan={onOpenPayPlanModel}
											/>
											<AssignmentStatusCardSection
												assignmentPhase={"Current"}
												selectedList={selectedList}
												isLoading={isPending}
												isDisabled={!VIEW_SALES_ASSOCIATE_PAY_PLANS}
												employee={employee}
												payPlanInfo={{
													payPlanId: employee?.currentPayPlanAssignment?.planId?._id,
													payPlanName: employee?.currentPayPlanAssignment?.planId?.name,
													acknowledgementDate: employee?.currentPayPlanAssignment?.acknowledgementDate,
													assignmentDate: employee?.currentPayPlanAssignment?.assignmentDate,
												}}
												onClickPayPlan={onOpenPayPlanModel}
											/>
										</Grid>
									</CardContent>
								</Stack>
							</Card>
						</Grid>
					);
				})}
			</Grid>
		</Stack>
	);
}
